import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useEffect, useMemo, useState } from "react";
import { Outcome, State } from "~/lib/pipeline";
import {
  filterSteps,
  FilterByOption,
  StateOrder,
} from "~/components/build/Show/components/Sidebar/useFilterStore";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import { CollapsibleState } from "~/components/build/Show/components/Sidebar/TreeItem/CollapsibleState";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";

export default function useExpandedIds() {
  const { build } = useBuild();
  const { steps } = useSteps();
  const expandedStateHeaders = useBuildPreferencesStore((state) => state.expandedStates || []);
  const expandedStepIds = useBuildStore((state) => state.expandedStepIds);
  const expandStep = useBuildStore((state) => state.expandStep);
  const setCollapsibleState = useBuildStore((state) => state.setCollapsibleState);
  const { currentStep, currentJob } = useCurrentJobOrStep();

  /**
   * Expand the current step and its group when the current job or step changes.
   */
  useEffect(() => {
    if (currentJob) {
      expandStep(currentJob.stepUuid);
    }

    if (currentJob?.groupUuid) {
      expandStep(currentJob.groupUuid);
    }

    if (currentStep) {
      expandStep(currentStep.uuid);
    }

    if (currentStep?.groupUuid) {
      expandStep(currentStep?.groupUuid);
    }
  }, [currentJob?.id, currentStep?.uuid]);

  /**
   * Find steps awaiting input
   */
  const stepsAwaitingInput = useMemo(() => {
    return steps.filter((step) => {
      return (
        step.type === "input" && step.state !== State.Finished && step.outcome !== Outcome.Errored
      );
    });
  }, [steps]);

  /**
   * Expand groups with children awaiting input (unless it's already been collapsed)
   */
  useEffect(() => {
    stepsAwaitingInput.map((step) => {
      if (step.groupUuid && !expandedStepIds.has(step.groupUuid)) {
        expandStep(step.groupUuid);
      }
    });
  }, [stepsAwaitingInput]);

  /**
   * Find steps with failures
   */
  const stepsWithFailures = useMemo(() => {
    return filterSteps(steps, [FilterByOption.Failed], build);
  }, [steps]);

  /**
   * Expand all steps with failures (unless it's already been collapsed)
   */
  useEffect(() => {
    stepsWithFailures.map((step) => !expandedStepIds.has(step.uuid) && expandStep(step.uuid));
  }, [stepsWithFailures]);

  /**
   * Keep expanded state headers in sync with expanded state
   */
  useEffect(() => {
    StateOrder.map((state) => {
      if (expandedStateHeaders.includes(state)) {
        setCollapsibleState(state, CollapsibleState.Expanded);
      } else {
        setCollapsibleState(state, CollapsibleState.Collapsed);
      }
    });
  }, [expandedStateHeaders]);

  /* Set the initial open state based on a few conditions:
   *
   *  - If the step is failed or failing
   *  - If this step or a child of this step is active
   *  - If any children are awaiting input (i.e. input steps)
   */
  const [ids, setIds] = useState<Map<string, CollapsibleState>>(
    new Map([
      [currentStep?.uuid ?? "", CollapsibleState.Expanded],
      [currentStep?.groupUuid ?? "", CollapsibleState.Expanded],
      ...stepsWithFailures.reduce(
        (acc, step) => acc.set(step.uuid, CollapsibleState.Expanded),
        new Map(),
      ),
      ...stepsAwaitingInput.reduce(
        (acc, step) => acc.set(step.uuid, CollapsibleState.Expanded),
        new Map(),
      ),
      ...expandedStateHeaders.reduce(
        (acc, state) => acc.set(state, CollapsibleState.Expanded),
        new Map(),
      ),
      ...expandedStepIds,
    ]),
  );

  useEffect(() => {
    setIds(new Map([...ids, ...expandedStepIds]));
  }, [expandedStepIds]);

  return ids;
}
